// src/pages/admin/AdminDashboard.js
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useRoleTheme } from "../../context/RoleThemeContext";
import {
  Users,
  Building2,
  FileText,
  Settings,
  Bell,
  Globe,
  Map,
  Layers,
  Mail,
  Monitor,
  Send,
  BarChart3,
  LayoutDashboard,
  KeyRound,
} from "lucide-react";

export default function AdminDashboard() {
  const { adminUser } = useAuth();
  const { roleTheme } = useRoleTheme();

 // console.log("AdminDashboard: Component rendered, adminUser:", adminUser);

  const allTiles = [
    { name: "Manage Main Company", path: "/admin/main-company", icon: <Building2 className="w-10 h-10 text-green-600" />, roles: ["A"] },
    { name: "Manage Users", path: "/admin/users", icon: <Users className="w-10 h-10 text-blue-600" />, roles: ["M", "A"] },
    { name: "Sales Reps", path: "/admin/sales-reps", icon: <Users className="w-10 h-10 text-gray-600" />, roles: ["A", "M"] },
    { name: "Company Setting", path: "/admin/company-settings", icon: <Settings className="w-10 h-10 text-gray-600" />, roles: ["M", "A"] },
    { name: "Global Default Template", path: "/admin/global-template", icon: <Globe className="w-10 h-10 text-indigo-600" />, roles: ["A"] },
    { name: "National Setting", path: "/admin/national-settings", icon: <Layers className="w-10 h-10 text-yellow-600" />, roles: ["A"] },
    { name: "State Setting", path: "/admin/state-settings", icon: <Map className="w-10 h-10 text-teal-600" />, roles: ["A"] },
    { name: "County Setting", path: "/admin/county-settings", icon: <Map className="w-10 h-10 text-cyan-600" />, roles: ["A"] },
    //  { name: "Latest Update & Features", path: "/admin/updates", icon: <Newspaper className="w-10 h-10 text-orange-600" /> },
    { name: "Notify Users", path: "/admin/notify-users", icon: <Bell className="w-10 h-10 text-red-600" />, roles: ["A","M"] },
    { name: "Transfer TitleRep", path: "/admin/transfer-titlerep", icon: <Send className="w-10 h-10 text-pink-600" />, roles: ["A"] },
    { name: "Tracking Summary", path: "/admin/tracking-summary", icon: <BarChart3 className="w-10 h-10 text-indigo-600" />, roles: ["M", "A"] },
    { name: "Manage Email Templates", path: "/admin/email-templates", icon: <Mail className="w-10 h-10 text-pink-500" />, roles: ["A"] },
    { name: "Dashboard Options", path: "/admin/dashboard-options", icon: <LayoutDashboard className="w-10 h-10 text-violet-600" />, roles: ["A", "M"] },
    { name: "Disclaimer", path: "/admin/disclaimer", icon: <FileText className="w-10 h-10 text-slate-600" />, roles: ["A", "M"] },
   { name: "Manage Themes", path: "/admin/themes/master", icon: <Layers className="w-10 h-10 text-gray-600" />, roles: ["A"] },
    { name: "Manage CMS", path: "/admin/cms", icon: <FileText className="w-10 h-10 text-purple-600" />, roles: ["A", "M"] },
    { name: "Manage Advertising", path: "/admin/advertise", icon: <Monitor className="w-10 h-10 text-blue-500" />, roles: ["A"] },
    { name: "Admin Password", path: "/admin/change-password", icon: <KeyRound className="w-10 h-10 text-amber-600" />, roles: ["A"] },

    //  { name: "Manage App Version", path: "/admin/app-version", icon: <AppWindow className="w-10 h-10 text-gray-500" /> },
  ];

  // Modify tile paths for master users to use /master prefix
  const getTilePath = (path) => {
    if (adminUser?.userType === "M") {
      // For master users, convert /admin prefix to /master prefix for specific allowed routes
      const masterAllowedRoutes = ["/admin/dashboard", "/admin/change-password", "/admin/users", "/admin/tracking-summary", "/admin/company-settings", "/admin/sales-reps", "/admin/notify-users", "/admin/dashboard-options", "/admin/cms", "/admin/disclaimer"];
      if (masterAllowedRoutes.includes(path)) {
        return path.replace("/admin", "/master");
      }
      // For dashboard, use /master/dashboard
      if (path === "/admin/dashboard") {
        return "/master/dashboard";
      }
    }
    return path;
  };

  // Filter tiles based on userType
  const tiles = adminUser?.userType === "A"
    ? allTiles.map(tile => ({ ...tile, path: getTilePath(tile.path) })) // Admin users see all tiles with original paths
    : allTiles.filter(tile => tile.roles?.includes("M")).map(tile => ({ ...tile, path: getTilePath(tile.path) })); // Master users see only allowed tiles with modified paths

  return (
    <div className="min-h-screen bg-gray-100 flex p-4">
      {/* Main Content */}
      <main className="flex-1 p-0">
        {/* Header */}
        <header className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800">Dashboard</h1>
          {/*<div className="flex items-center gap-4">
            {adminUser && (
              <span className="text-gray-700">
                Hi, {adminUser.first_name} {adminUser.last_name}
              </span>
            )}
            <button
              onClick={handleLogout}
              className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition"
            >
              <FiLogOut className="w-4 h-4" /> Logout
            </button>
          </div>*/}
        </header>

        {/* Dashboard Tiles */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {tiles.map((tile, index) => (
            <Link
              key={index}
              to={tile.path}
              className={`bg-white rounded-2xl shadow-md p-6 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 hover:scale-105 border border-gray-100`}
              style={{
                background: roleTheme?.gradientBg?.primary || 'linear-gradient(135deg, #eff6ff 0%, #e0e7ff 100%)',
                borderColor: roleTheme?.colors?.sidebar?.hover || '#e5e7eb'
              }}
            >
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-800">{tile.name}</h2>
                <div className="text-gray-600">
                  {tile.icon}
                </div>
              </div>
              <p className="text-gray-600 mt-2">Manage {tile.name.toLowerCase()} here.</p>
            </Link>
          ))}
        </div>
      </main>
    </div>
  );
}
